import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useApi } from './useApi';

/**
 * Authentication composable for managing user authentication state and operations
 * @returns {Object} Authentication state and methods
 */
export function useAuth() {
  const router = useRouter();
  const { post } = useApi();

  // State
  const isAuthenticated = ref(!!localStorage.getItem('token'));
  const username = ref(localStorage.getItem('username') || '');
  const role = ref(localStorage.getItem('role') || 'user');
  const loading = ref(false);
  const error = ref('');

  // Computed
  const isAdmin = computed(() => role.value === 'admin');
  const token = computed(() => localStorage.getItem('token'));

  /**
   * Login user
   * @param {string} usernameInput - Username
   * @param {string} password - Password
   * @returns {Promise<boolean>} Success status
   */
  const login = async (usernameInput, password) => {
    loading.value = true;
    error.value = '';

    try {
      const response = await post('/auth/login', {
        username: usernameInput,
        password
      });

      if (response.success && response.token) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('username', response.user.username);
        localStorage.setItem('role', response.user.role || 'user');

        isAuthenticated.value = true;
        username.value = response.user.username;
        role.value = response.user.role;

        return true;
      }

      throw new Error(response.message || 'Login failed');
    } catch (err) {
      error.value = err.response?.data?.message || err.message || 'Login failed';
      return false;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Register new user
   * @param {string} usernameInput - Username
   * @param {string} password - Password
   * @returns {Promise<boolean>} Success status
   */
  const register = async (usernameInput, password) => {
    loading.value = true;
    error.value = '';

    try {
      const response = await post('/auth/register', {
        username: usernameInput,
        password
      });

      if (response.success && response.token) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('username', response.user.username);
        localStorage.setItem('role', response.user.role || 'user');

        isAuthenticated.value = true;
        username.value = response.user.username;
        role.value = response.user.role;

        return true;
      }

      throw new Error(response.message || 'Registration failed');
    } catch (err) {
      error.value = err.response?.data?.message || err.message || 'Registration failed';
      
      // Handle validation errors
      if (err.response?.data?.errors) {
        const errorMessages = err.response.data.errors.map(e => e.message).join(', ');
        error.value = errorMessages;
      }

      return false;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Logout user
   */
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('role');

    isAuthenticated.value = false;
    username.value = '';
    role.value = 'user';

    router.push('/login');
  };

  /**
   * Check if user is authenticated (for route guards)
   * @returns {boolean} Authentication status
   */
  const checkAuth = () => {
    const hasToken = !!localStorage.getItem('token');
    isAuthenticated.value = hasToken;
    return hasToken;
  };

  return {
    // State
    isAuthenticated,
    username,
    role,
    loading,
    error,

    // Computed
    isAdmin,
    token,

    // Methods
    login,
    register,
    logout,
    checkAuth
  };
}
